import { useState, useEffect } from 'react';
import { config } from '@/config';

export interface GalleryImage {
  src: string;
  alt: string;
  caption: string;
}

const MAX_IMAGES = 20;

export function fileNameToCaption(filename: string): string {
  const name = filename
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/^\d+\s*/, '')
    .trim();

  if (!name) return 'Ashok Telecom — Our Store';

  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Try to fetch image list from the PHP endpoint.
 * Falls back to sequential numbered scanning if PHP is not available.
 */
export async function loadGalleryImages(): Promise<GalleryImage[]> {
  const baseUrl = config.gallery.url;
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const listUrl = config.gallery.listUrl;

  // Try PHP list endpoint first
  if (listUrl) {
    try {
      const response = await fetch(listUrl, { method: 'GET', cache: 'no-store' });
      if (response.ok) {
        const filenames = (await response.json()) as string[];
        if (Array.isArray(filenames) && filenames.length > 0) {
          return filenames
            .slice(0, MAX_IMAGES)
            .map((filename) => ({
              src: `${normalizedBase}${filename}`,
              alt: fileNameToCaption(filename),
              caption: fileNameToCaption(filename),
            }));
        }
      }
    } catch {
      // PHP endpoint not available — fall through to fallback
    }
  }

  // Fallback: try sequential numbers 1..50 across common extensions
  const EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
  const MAX_ATTEMPTS = 50;
  const candidates: GalleryImage[] = [];

  for (let i = 1; i <= MAX_ATTEMPTS; i++) {
    EXTENSIONS.forEach((ext) => {
      const filename = `${i}.${ext}`;
      candidates.push({
        src: `${normalizedBase}${filename}`,
        alt: fileNameToCaption(filename),
        caption: fileNameToCaption(filename),
      });
    });
  }

  // Also try common camera patterns
  const patterns = ['IMG', 'DSC', 'PXL', 'IMG_', 'DSC_', 'PXL_'];
  for (let i = 1; i <= 20; i++) {
    const padded = String(i).padStart(4, '0');
    patterns.forEach((prefix) => {
      EXTENSIONS.forEach((ext) => {
        const filename = `${prefix}${padded}.${ext}`;
        candidates.push({
          src: `${normalizedBase}${filename}`,
          alt: fileNameToCaption(filename),
          caption: fileNameToCaption(filename),
        });
      });
    });
  }

  return candidates;
}

/**
 * React hook that loads gallery images and filters broken ones.
 */
export function useGalleryImages(): {
  images: GalleryImage[];
  loading: boolean;
  error: string | null;
} {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadGalleryImages()
      .then((candidates) => {
        if (cancelled) return;
        if (candidates.length === 0) {
          setImages([]);
          setLoading(false);
          return;
        }

        const valid: GalleryImage[] = [];
        let checked = 0;

        candidates.forEach((candidate) => {
          const img = new Image();
          img.onload = () => {
            if (cancelled) return;
            if (valid.length < MAX_IMAGES && !valid.find((v) => v.src === candidate.src)) {
              valid.push(candidate);
              setImages([...valid]);
            }
            checked++;
            if (checked === candidates.length) setLoading(false);
          };
          img.onerror = () => {
            if (cancelled) return;
            checked++;
            if (checked === candidates.length) setLoading(false);
          };
          img.src = candidate.src;
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load gallery');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { images, loading, error };
}

export const heroFallbackImage: GalleryImage = {
  src: 'https://public.readdy.ai/ai/img_res/edited_28daf58bba0fa75f016e087db6ed4af9_711691ea.jpg',
  alt: 'Ashok Communication Storefront',
  caption: 'Ashok Telecom — Your trusted electronics partner',
};